import { X, Save, Upload, Loader2, FileText, Type, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import RichTextEditor from "@/components/RichTextEditor";
import { useAdmin } from "./AdminContext";

export default function AdminModal() {
  const {
    modalOpen,
    setModalOpen,
    editingItem,
    formData,
    setFormData,
    uploading,
    fileInputRef,
    handleSave,
    handleFileUpload,
    data,
  } = useAdmin();

  return (
    <Dialog open={modalOpen} onOpenChange={setModalOpen}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingItem?.isNew ? "Create" : "Edit"} {editingItem?.type?.replace("-", " ")}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Pillars Form */}
          {editingItem?.type === "pillars" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Title</label>
                  <Input
                    value={formData.title || ""}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    data-testid="form-title"
                  />
                </div>
                <div>
                  <label className="form-label">Slug</label>
                  <Input
                    value={formData.slug || ""}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    data-testid="form-slug"
                  />
                </div>
              </div>
              <div>
                <label className="form-label">Description</label>
                <Textarea
                  value={formData.description || ""}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  data-testid="form-description"
                />
              </div>
              <div>
                <label className="form-label flex items-center gap-2">
                  <Type className="w-4 h-4" />
                  Purpose (Rich Text)
                </label>
                <RichTextEditor
                  value={formData.purpose || ""}
                  onChange={(html) => setFormData({...formData, purpose: html})}
                  placeholder="Describe the purpose of this pillar..."
                  minHeight="120px"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Icon</label>
                  <Input
                    value={formData.icon || ""}
                    onChange={(e) => setFormData({...formData, icon: e.target.value})}
                  />
                </div>
                <div>
                  <label className="form-label">Color</label>
                  <Input
                    type="color"
                    value={formData.color || "#F25F4C"}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                  />
                </div>
                <div>
                  <label className="form-label">Order</label>
                  <Input
                    type="number"
                    value={formData.order || 0}
                    onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                  />
                </div>
              </div>
            </>
          )}

          {/* Protocols Form */}
          {editingItem?.type === "protocols" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Title</label>
                  <Input
                    value={formData.title || ""}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                  />
                </div>
                <div>
                  <label className="form-label">Slug</label>
                  <Input
                    value={formData.slug || ""}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="form-label">Summary</label>
                <Textarea
                  value={formData.summary || ""}
                  onChange={(e) => setFormData({...formData, summary: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Pillar</label>
                  <Select 
                    value={formData.pillar_id} 
                    onValueChange={(v) => setFormData({...formData, pillar_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select pillar" />
                    </SelectTrigger>
                    <SelectContent>
                      {data.pillars.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="form-label">Urgency</label>
                  <Select 
                    value={formData.urgency} 
                    onValueChange={(v) => setFormData({...formData, urgency: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="crisis">Crisis</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="routine">Routine</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="form-label flex items-center gap-2">
                  <Type className="w-4 h-4" />
                  Triggers (Rich Text)
                </label>
                <RichTextEditor
                  value={formData.triggers || ""}
                  onChange={(html) => setFormData({...formData, triggers: html})}
                  placeholder="Describe when to use this protocol..."
                  minHeight="120px"
                />
              </div>
              <div>
                <label className="form-label flex items-center gap-2">
                  <Type className="w-4 h-4" />
                  Implementation Notes (Rich Text)
                </label>
                <RichTextEditor
                  value={formData.implementation_notes || ""}
                  onChange={(html) => setFormData({...formData, implementation_notes: html})}
                  placeholder="Tips, common pitfalls, best practices..."
                  minHeight="120px"
                />
              </div>
              <div>
                <label className="form-label flex items-center gap-2">
                  <Type className="w-4 h-4" />
                  Compliance Notes (Rich Text)
                </label>
                <RichTextEditor
                  value={formData.compliance_notes || ""}
                  onChange={(html) => setFormData({...formData, compliance_notes: html})}
                  placeholder="Regulatory or compliance considerations..."
                  minHeight="100px"
                />
              </div>
            </>
          )}

          {/* Assets Form */}
          {editingItem?.type === "assets" && (
            <>
              <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  accept=".pdf,.docx,.xlsx,.pptx,.doc,.xls,.ppt,.txt,.csv,.zip"
                  className="hidden"
                  id="file-upload"
                  data-testid="file-upload-input"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
                      <span className="text-sm text-slate-600">Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-10 h-10 text-slate-400" />
                      <span className="text-sm font-medium text-slate-700">
                        Click to upload a file
                      </span>
                      <span className="text-xs text-slate-500">
                        PDF, DOCX, XLSX, PPTX, TXT, CSV, ZIP (max 50MB)
                      </span>
                    </div>
                  )}
                </label>
              </div>

              {formData.file_url && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-emerald-800 truncate">
                      File uploaded successfully
                    </p>
                    <p className="text-xs text-emerald-600 truncate">
                      {formData.file_url}
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="form-label">Title *</label>
                <Input
                  value={formData.title || ""}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Enter asset title"
                  data-testid="asset-title-input"
                />
              </div>
              <div>
                <label className="form-label">Description</label>
                <Textarea
                  value={formData.description || ""}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe this asset..."
                  data-testid="asset-description-input"
                />
              </div>
              <div>
                <label className="form-label">File URL (or enter manually)</label>
                <Input
                  value={formData.file_url || ""}
                  onChange={(e) => setFormData({...formData, file_url: e.target.value})}
                  placeholder="URL will be set after upload, or enter manually"
                  data-testid="asset-url-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">File Type</label>
                  <Select 
                    value={formData.file_type} 
                    onValueChange={(v) => setFormData({...formData, file_type: v})}
                  >
                    <SelectTrigger data-testid="asset-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="docx">DOCX</SelectItem>
                      <SelectItem value="xlsx">XLSX</SelectItem>
                      <SelectItem value="pptx">PPTX</SelectItem>
                      <SelectItem value="doc">DOC</SelectItem>
                      <SelectItem value="xls">XLS</SelectItem>
                      <SelectItem value="txt">TXT</SelectItem>
                      <SelectItem value="csv">CSV</SelectItem>
                      <SelectItem value="zip">ZIP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="form-label">Version</label>
                  <Input
                    value={formData.version || "1.0"}
                    onChange={(e) => setFormData({...formData, version: e.target.value})}
                    data-testid="asset-version-input"
                  />
                </div>
              </div>
            </>
          )}

          {/* Generic form for toolkits, data-guides, training */}
          {["toolkits", "data-guides", "training"].includes(editingItem?.type) && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Title</label>
                  <Input
                    value={formData.title || ""}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                  />
                </div>
                <div>
                  <label className="form-label">Slug</label>
                  <Input
                    value={formData.slug || ""}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  />
                </div>
              </div>
              {editingItem?.type === "toolkits" && (
                <div>
                  <label className="form-label">Summary</label>
                  <Textarea
                    value={formData.summary || ""}
                    onChange={(e) => setFormData({...formData, summary: e.target.value})}
                  />
                </div>
              )}
              {editingItem?.type === "data-guides" && (
                <>
                  <div>
                    <label className="form-label">Domain</label>
                    <Input
                      value={formData.domain || ""}
                      onChange={(e) => setFormData({...formData, domain: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Type className="w-4 h-4" />
                      How to Interpret (Rich Text)
                    </label>
                    <RichTextEditor
                      value={formData.how_to_interpret || ""}
                      onChange={(html) => setFormData({...formData, how_to_interpret: html})}
                      placeholder="Explain how to interpret this data..."
                      minHeight="150px"
                    />
                  </div>
                </>
              )}
              {editingItem?.type === "training" && (
                <>
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Type className="w-4 h-4" />
                      Objective (Rich Text)
                    </label>
                    <RichTextEditor
                      value={formData.objective || ""}
                      onChange={(html) => setFormData({...formData, objective: html})}
                      placeholder="What will learners achieve..."
                      minHeight="120px"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Time Estimate</label>
                      <Input
                        value={formData.time_estimate || ""}
                        onChange={(e) => setFormData({...formData, time_estimate: e.target.value})}
                        placeholder="e.g., 30 minutes"
                      />
                    </div>
                    <div>
                      <label className="form-label">Video URL</label>
                      <Input
                        value={formData.video_url || ""}
                        onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => setModalOpen(false)}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} className="btn-primary" data-testid="save-btn">
            <Save className="w-4 h-4 mr-2" />
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
